import { useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { useLanguage } from "@/i18n/LanguageContext";
import Lightbox from "@/components/site/Lightbox";

// Editorial bento layout — 10 items, asymmetric
const items = [
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/z74sjidm_953B12AA-EEF2-4BEE-9396-239E774F0FC4.png",
    alt: "Esterno dell'hotel",
    span: "col-span-12 md:col-span-7 row-span-2",
    h: "h-[360px] md:h-[680px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/zb8fq8zt_DSC00051%20ed.jpg",
    alt: "Reception dell'hotel",
    span: "col-span-6 md:col-span-5",
    h: "h-[200px] md:h-[310px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/wu7dqmiz_DSC00041%20ed.jpg",
    alt: "Chiavi delle camere",
    span: "col-span-6 md:col-span-5",
    h: "h-[200px] md:h-[310px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/x53c6gh4_DSC00123%20ed.jpg",
    alt: "Bagno spazioso con doccia",
    span: "col-span-12 md:col-span-7",
    h: "h-[360px] md:h-[640px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/s8kc0a3l_DSC00054%20ed.jpg",
    alt: "Scala interna con dettagli",
    span: "col-span-12 md:col-span-5",
    h: "h-[420px] md:h-[640px]",
    objectPosition: "left center",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/xjl01z8k_B5F190F3-5C71-4FC2-9866-9FA89F6EE1D9.png",
    alt: "Camera matrimoniale",
    span: "col-span-12 md:col-span-4",
    h: "h-[320px] md:h-[380px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/gs147ol3_F8B60554-37B2-4455-86BB-B09684034DD4.png",
    alt: "Camera singola",
    span: "col-span-6 md:col-span-4",
    h: "h-[320px] md:h-[380px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/l3vohthe_2D8AC323-2047-41B3-AE3E-5A888E641B33.png",
    alt: "Camera tripla con tre letti",
    span: "col-span-6 md:col-span-4",
    h: "h-[320px] md:h-[380px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/c0ro5ccw_DSC00056%20ed.jpg",
    alt: "Libreria dell'hotel",
    span: "col-span-12 md:col-span-4",
    h: "h-[360px] md:h-[420px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/ujxsxsh9_DSC00146%20ed.jpg",
    alt: "Vista dalla finestra con palme",
    span: "col-span-12 md:col-span-4",
    h: "h-[360px] md:h-[420px]",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_novi-hotel/artifacts/f10iw2jk_DSC00167%20ed.jpg",
    alt: "Bagno con doccia in pietra",
    span: "col-span-12 md:col-span-4",
    h: "h-[360px] md:h-[420px]",
  },
];

export default function Gallery() {
  const headRef = useReveal();
  const gridRef = useReveal();
  const { t } = useLanguage();
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox((s) => ({ ...s, open: false }));

  return (
    <section
      id="galleria"
      data-testid="gallery-section"
      className="py-24 md:py-36 bg-[#FAF9F5]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div ref={headRef} className="reveal max-w-3xl mb-20 md:mb-28">
          <span className="overline block mb-5">{t.gallery.overline}</span>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light tracking-tight text-[#1A1C19] leading-[1.02]">
            {t.gallery.titleLine1}
            {t.gallery.titleLine2 && (
              <span className="italic block text-[#363A33]">
                {t.gallery.titleLine2}
              </span>
            )}
          </h2>
          {t.gallery.description && (
            <p className="mt-6 text-base md:text-lg text-[#5C6058] leading-relaxed">
              {t.gallery.description}
            </p>
          )}
        </div>

        <div
          ref={gridRef}
          className="reveal grid grid-cols-12 gap-3 md:gap-5"
        >
          {items.map((it, i) => (
            <figure
              key={i}
              data-testid={`gallery-item-${i}`}
              className={`image-zoom cursor-pointer ${it.span}`}
              onClick={() => openLightbox(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openLightbox(i);
                }
              }}
              aria-label={`Apri foto: ${it.alt}`}
            >
              <img
                src={it.src}
                alt={it.alt}
                loading="lazy"
                className={`w-full ${it.h} object-cover`}
                style={it.objectPosition ? { objectPosition: it.objectPosition } : undefined}
              />
            </figure>
          ))}
        </div>
      </div>

      <Lightbox
        images={items}
        initialIndex={lightbox.index}
        isOpen={lightbox.open}
        onClose={closeLightbox}
      />
    </section>
  );
}
